import { useState, useEffect, useRef, useCallback } from "react";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY;

// When no site key is configured (e.g. local development), reCAPTCHA is effectively
// disabled on the client: the hook reports ready immediately and executeRecaptcha
// resolves with `null`. Callers already guard with `if (recaptchaToken)` before
// attaching the token to requests, and the backend skips verification when no
// token is sent (or when RECAPTCHA_V3_SECRET_KEY is unset), so everything works
// end-to-end without reCAPTCHA in dev.
const RECAPTCHA_DISABLED = !RECAPTCHA_SITE_KEY;

// Global state so script loads only once across the app
let globalScriptLoaded = false;
let globalReadyCallbacks = [];

function triggerReady() {
  globalReadyCallbacks.forEach((cb) => cb());
  globalReadyCallbacks = [];
}

/**
 * Reusable hook for Google reCAPTCHA v3.
 * Loads the script once per app; use on any page that needs captcha (Login, Forgot Password, Sign Up, etc.).
 *
 * When REACT_APP_RECAPTCHA_V3_SITE_KEY is not set, reCAPTCHA is disabled:
 *   - `isReady` is `true` (so submit buttons aren't stuck on "Loading...")
 *   - `executeRecaptcha` resolves with `null` (no token attached to requests)
 *
 * @returns {{
 *   executeRecaptcha: (action: string) => Promise<string | null>,
 *   isReady: boolean,
 *   error: string | null,
 *   resetError: () => void,
 *   siteKey: string | undefined,
 *   isDisabled: boolean
 * }}
 */
export function useRecaptchaV3() {
  const [isReady, setIsReady] = useState(
    RECAPTCHA_DISABLED ||
      (globalScriptLoaded && typeof window !== "undefined" && !!window.grecaptcha)
  );
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (RECAPTCHA_DISABLED) {
      // Dev / unconfigured environment: act as a no-op.
      if (mountedRef.current) setIsReady(true);
      return () => {
        mountedRef.current = false;
      };
    }

    if (globalScriptLoaded && window.grecaptcha) {
      window.grecaptcha.ready(() => {
        if (mountedRef.current) setIsReady(true);
      });
      return;
    }

    const onReady = () => {
      if (mountedRef.current) setIsReady(true);
    };
    globalReadyCallbacks.push(onReady);

    const script = document.querySelector(`script[src*="recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}"]`);
    if (script) {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => triggerReady());
      }
      return () => {
        globalReadyCallbacks = globalReadyCallbacks.filter((cb) => cb !== onReady);
      };
    }

    const el = document.createElement("script");
    el.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    el.async = true;
    el.onload = () => {
      globalScriptLoaded = true;
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => triggerReady());
      }
    };
    el.onerror = () => {
      if (mountedRef.current) {
        setError("Security check failed to load. Please refresh the page.");
      }
    };
    document.head.appendChild(el);

    return () => {
      mountedRef.current = false;
      globalReadyCallbacks = globalReadyCallbacks.filter((cb) => cb !== onReady);
    };
  }, []);

  const executeRecaptcha = useCallback(
    (action) => {
      // Disabled mode: resolve with null so callers skip attaching the token.
      if (RECAPTCHA_DISABLED) {
        return Promise.resolve(null);
      }
      if (!window.grecaptcha) {
        return Promise.reject(new Error("reCAPTCHA not available"));
      }
      return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: action || "submit" });
    },
    []
  );

  const resetError = useCallback(() => setError(null), []);

  return {
    executeRecaptcha,
    isReady,
    error,
    resetError,
    siteKey: RECAPTCHA_SITE_KEY || undefined,
    isDisabled: RECAPTCHA_DISABLED,
  };
}

export default useRecaptchaV3;
